'use client';

import { cva } from 'class-variance-authority';
import { useMemo } from 'react';

import { Dialog } from '@/components/atoms';
import { useBreakpoint } from '@/lib/hooks/util/useBreakpoint';
import { cn } from '@/lib/utils/tailwind';

import type { ReactNode, Ref } from 'react';

const sheetVariants = cva(
  ['fixed', 'z-1600', 'bg-(--color-surface-neutral-softer)', 'shadow-lg'],
  {
    variants: {
      side: {
        right: ['inset-y-0', 'right-0', 'h-[100vh]', 'sm:max-w-sm'],
        left: ['inset-y-0', 'left-0', 'h-[100vh]', 'sm:max-w-sm'],
        bottom: [
          'inset-x-0',
          'bottom-0',
          'w-full',
          'h-auto',
          'overflow-y-scroll',
          'max-h-[100vh]',
        ],
      },
      size: {
        sm: 'w-full sm:w-1/4',
        md: 'w-full sm:w-1/2',
        lg: 'w-full sm:w-3/4',
        full: 'w-full',
      },
    },
    defaultVariants: {
      side: 'right',
      size: 'md',
    },
  },
);

type SheetProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  side?: 'left' | 'right' | 'bottom';
  size?: 'sm' | 'md' | 'lg' | 'full';
  children: ReactNode;
  className?: string;
  ref?: Ref<HTMLDivElement>;
};

const Sheet = ({
  isOpen,
  onOpenChange,
  side,
  size = 'md',
  children,
  className,
  ref,
}: SheetProps) => {
  const isDesktop = useBreakpoint('md');

  const effectiveSide = useMemo<'left' | 'right' | 'bottom'>(() => {
    if (side) return side;
    return isDesktop ? 'right' : 'bottom';
  }, [side, isDesktop]);

  const animationIn =
    effectiveSide === 'right'
      ? 'slide-in-from-right 500ms ease-out forwards'
      : effectiveSide === 'left'
        ? 'slide-in-from-left 500ms ease-out forwards'
        : 'slide-in-from-bottom 500ms ease-out forwards';

  const animationOut =
    effectiveSide === 'right'
      ? 'slide-out-to-right 300ms ease-in forwards'
      : effectiveSide === 'left'
        ? 'slide-out-to-left 300ms ease-in forwards'
        : 'slide-out-to-bottom 300ms ease-in forwards';

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange} ref={ref}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content
          className={cn(
            sheetVariants({ side: effectiveSide, size }),
            className,
          )}
          style={{ animation: isOpen ? animationIn : animationOut }}
        >
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export { Sheet, type SheetProps };
